import React, { useEffect, useState, createContext } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, ShoppingCartIcon, UserIcon, MenuIcon, XIcon, LogOutIcon, HeartIcon, PackageIcon, SettingsIcon } from 'lucide-react';
// Create a simple cart context that can be used across components
export const CartContext = createContext({
  cartItems: [],
  addToCart: item => {},
  removeFromCart: id => {},
  cartCount: 0
});
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  // Function to add items to cart
  const addToCart = item => {
    setCartItems(prevItems => {
      // Check if item already exists in cart
      const existingItem = prevItems.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        // Increase quantity if item already exists
        return prevItems.map(cartItem => cartItem.id === item.id ? {
          ...cartItem,
          quantity: cartItem.quantity + 1
        } : cartItem);
      } else {
        // Add new item with quantity 1
        return [...prevItems, {
          ...item,
          quantity: 1
        }];
      }
    });
  };
  // Function to remove items from cart
  const removeFromCart = id => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== id));
  };
  // Calculate total items in cart
  const cartCount = cartItems.reduce((total, item) => total + item.quantity, 0);
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (isCartOpen && !event.target.closest('.cart-dropdown')) {
        setIsCartOpen(false);
      }
      if (isAccountOpen && !event.target.closest('.account-dropdown')) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCartOpen, isAccountOpen]);
  return <header className="bg-white shadow-sm sticky top-0 z-50" data-id="element-105">
      <div className="container mx-auto px-4 py-4" data-id="element-106">
        <div className="flex items-center justify-between" data-id="element-107">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold text-green-700" data-id="element-108">
            MarketHub
          </Link>
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8" data-id="element-109">
            <Link to="/" className="text-gray-700 hover:text-green-600 font-medium" data-id="element-110">
              Home
            </Link>
            <Link to="/categories" className="text-gray-700 hover:text-green-600 font-medium" data-id="element-111">
              Categories
            </Link>
            <Link to="/become-seller" className="text-gray-700 hover:text-green-600 font-medium" data-id="element-112">
              Sell with Us
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-green-600 font-medium" data-id="element-113">
              About
            </Link>
            <Link to="/contact" className="text-gray-700 hover:text-green-600 font-medium" data-id="element-114">
              Contact
            </Link>
          </nav>
          {/* Search and Icons */}
          <div className="hidden md:flex items-center space-x-6" data-id="element-115">
            <div className="relative" data-id="element-116">
              <input type="text" placeholder="Search products..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-64" data-id="element-117" />
              <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-118" />
            </div>
            {/* Cart Button with Dropdown */}
            <div className="relative cart-dropdown" data-id="element-119">
              <button className="text-gray-700 hover:text-green-600 relative" onClick={() => {
              setIsCartOpen(!isCartOpen);
              setIsAccountOpen(false);
            }} data-id="element-120">
                <ShoppingCartIcon className="h-6 w-6" data-id="element-121" />
                {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-green-700 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-id="element-122">
                    {cartCount}
                  </span>}
              </button>
              {/* Cart Dropdown */}
              {isCartOpen && <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50" data-id="element-123">
                  <div className="p-4 border-b border-gray-200" data-id="element-124">
                    <h3 className="font-medium" data-id="element-125">Shopping Cart ({cartCount})</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto" data-id="element-126">
                    {cartItems.length === 0 ? <div className="p-4 text-center text-gray-500" data-id="element-127">
                        Your cart is empty
                      </div> : <div data-id="element-128">
                        {cartItems.map(item => <div key={item.id} className="p-4 border-b border-gray-100 flex" data-id="element-129">
                            <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0" data-id="element-130">
                              <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-131" />
                            </div>
                            <div className="ml-3 flex-grow" data-id="element-132">
                              <div className="font-medium text-sm" data-id="element-133">
                                {item.name}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-134">
                                ${item.price.toFixed(2)} x {item.quantity}
                              </div>
                              <button className="text-xs text-red-500 mt-1" onClick={() => removeFromCart(item.id)} data-id="element-135">
                                Remove
                              </button>
                            </div>
                          </div>)}
                      </div>}
                  </div>
                  <div className="p-4 border-t border-gray-200" data-id="element-136">
                    <div className="flex justify-between mb-3" data-id="element-137">
                      <span className="font-medium" data-id="element-138">Total:</span>
                      <span className="font-bold" data-id="element-139">
                        $
                        {cartItems.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2)}
                      </span>
                    </div>
                    <Link to="/cart" className="block w-full bg-green-700 hover:bg-green-800 text-white text-center py-2 rounded-md font-medium" onClick={() => setIsCartOpen(false)} data-id="element-140">
                      View Cart
                    </Link>
                    <Link to="/checkout" className="block w-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-center py-2 rounded-md font-medium mt-2" onClick={() => setIsCartOpen(false)} data-id="element-141">
                      Checkout
                    </Link>
                  </div>
                </div>}
            </div>
            {/* Account Button with Dropdown */}
            <div className="relative account-dropdown" data-id="element-142">
              <button className="text-gray-700 hover:text-green-600" onClick={() => {
              setIsAccountOpen(!isAccountOpen);
              setIsCartOpen(false);
            }} data-id="element-143">
                <UserIcon className="h-6 w-6 text-green-700 hover:text-green-500" data-id="element-144" />
              </button>
              {/* Account Dropdown */}
              {isAccountOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50" data-id="element-145">
                  <div className="p-3 border-b border-gray-200" data-id="element-146">
                    <div className="font-medium" data-id="element-147">John Doe</div>
                    <div className="text-sm text-gray-500" data-id="element-148">
                      john.doe@example.com
                    </div>
                  </div>
                  <div data-id="element-149">
                    <Link to="/account/profile" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center" onClick={() => setIsAccountOpen(false)} data-id="element-150">
                      <UserIcon className="h-4 w-4 mr-2" data-id="element-151" />
                      My Profile
                    </Link>
                    <Link to="/account/orders" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center" onClick={() => setIsAccountOpen(false)} data-id="element-152">
                      <PackageIcon className="h-4 w-4 mr-2" data-id="element-153" />
                      Orders
                    </Link>
                    <Link to="/account/wishlist" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center" onClick={() => setIsAccountOpen(false)} data-id="element-154">
                      <HeartIcon className="h-4 w-4 mr-2" data-id="element-155" />
                      Wishlist
                    </Link>
                    <Link to="/account/settings" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center" onClick={() => setIsAccountOpen(false)} data-id="element-156">
                      <SettingsIcon className="h-4 w-4 mr-2" data-id="element-157" />
                      Settings
                    </Link>
                    <div className="border-t border-gray-100" data-id="element-158">
                      <Link to="/logout" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center text-red-500" onClick={() => {
                    setIsAccountOpen(false);
                    // Add logout logic here
                    alert('You have been logged out');
                  }} data-id="element-159">
                        <LogOutIcon className="h-4 w-4 mr-2" data-id="element-160" />
                        Logout
                      </Link>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          {/* Mobile Menu Button */}
          <button className="md:hidden text-gray-700" onClick={() => setIsMenuOpen(!isMenuOpen)} data-id="element-161">
            {isMenuOpen ? <XIcon className="h-6 w-6" data-id="element-162" /> : <MenuIcon className="h-6 w-6" data-id="element-163" />}
          </button>
        </div>
        {/* Mobile Search */}
        <div className="mt-4 md:hidden" data-id="element-164">
          <div className="relative" data-id="element-165">
            <input type="text" placeholder="Search products..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full" data-id="element-166" />
            <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-167" />
          </div>
        </div>
        {/* Mobile Navigation */}
        {isMenuOpen && <nav className="mt-4 md:hidden space-y-3 pb-3" data-id="element-168">
            <Link to="/" className="block text-gray-700 hover:text-green-600 font-medium" onClick={() => setIsMenuOpen(false)} data-id="element-169">
              Home
            </Link>
            <Link to="/categories" className="block text-gray-700 hover:text-green-600 font-medium" onClick={() => setIsMenuOpen(false)} data-id="element-170">
              Categories
            </Link>
            <Link to="/become-seller" className="block text-gray-700 hover:text-green-600 font-medium" onClick={() => setIsMenuOpen(false)} data-id="element-171">
              Sell with Us
            </Link>
            <Link to="/about" className="block text-gray-700 hover:text-green-600 font-medium" onClick={() => setIsMenuOpen(false)} data-id="element-172">
              About
            </Link>
            <Link to="/contact" className="block text-gray-700 hover:text-green-600 font-medium" onClick={() => setIsMenuOpen(false)} data-id="element-173">
              Contact
            </Link>
            <div className="flex space-x-4 pt-2" data-id="element-174">
              <div className="relative" data-id="element-175">
                <button className="text-gray-700 hover:text-green-600 relative" onClick={() => {
              setIsCartOpen(!isCartOpen);
              setIsAccountOpen(false);
            }} data-id="element-176">
                  <ShoppingCartIcon className="h-6 w-6" data-id="element-177" />
                  {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-green-700 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-id="element-178">
                      {cartCount}
                    </span>}
                </button>
              </div>
              <button className="text-gray-700 hover:text-green-600" onClick={() => {
            setIsAccountOpen(!isAccountOpen);
            setIsCartOpen(false);
          }} data-id="element-179">
                <UserIcon className="h-6 w-6 text-green-700 hover:text-green-500" data-id="element-180" />
              </button>
            </div>
          </nav>}
        {/* Mobile Cart Dropdown */}
        {isCartOpen && isMenuOpen && <div className="mt-3 bg-white rounded-lg shadow-lg border border-gray-200" data-id="element-181">
            <div className="p-4 border-b border-gray-200" data-id="element-182">
              <h3 className="font-medium" data-id="element-183">Shopping Cart ({cartCount})</h3>
            </div>
            <div className="max-h-64 overflow-y-auto" data-id="element-184">
              {cartItems.length === 0 ? <div className="p-4 text-center text-gray-500" data-id="element-185">
                  Your cart is empty
                </div> : <div data-id="element-186">
                  {cartItems.map(item => <div key={item.id} className="p-4 border-b border-gray-100 flex" data-id="element-187">
                      <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0" data-id="element-188">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-189" />
                      </div>
                      <div className="ml-3 flex-grow" data-id="element-190">
                        <div className="font-medium text-sm" data-id="element-191">{item.name}</div>
                        <div className="text-sm text-gray-500" data-id="element-192">
                          ${item.price.toFixed(2)} x {item.quantity}
                        </div>
                        <button className="text-xs text-red-500 mt-1" onClick={() => removeFromCart(item.id)} data-id="element-193">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div>}
            </div>
            <div className="p-4 border-t border-gray-200" data-id="element-194">
              <Link to="/cart" className="block w-full bg-green-700 hover:bg-green-800 text-white text-center py-2 rounded-md font-medium" onClick={() => {
            setIsCartOpen(false);
            setIsMenuOpen(false);
          }} data-id="element-195">
                View Cart
              </Link>
            </div>
          </div>}
        {/* Mobile Account Dropdown */}
        {isAccountOpen && isMenuOpen && <div className="mt-3 bg-white rounded-lg shadow-lg border border-gray-200" data-id="element-196">
            <div className="p-3 border-b border-gray-200" data-id="element-197">
              <div className="font-medium" data-id="element-198">John Doe</div>
              <div className="text-sm text-gray-500" data-id="element-199">john.doe@example.com</div>
            </div>
            <div data-id="element-200">
              <Link to="/account/profile" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center" onClick={() => {
            setIsAccountOpen(false);
            setIsMenuOpen(false);
          }} data-id="element-201">
                <UserIcon className="h-4 w-4 mr-2" data-id="element-202" />
                My Profile
              </Link>
              <Link to="/account/orders" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center" onClick={() => {
            setIsAccountOpen(false);
            setIsMenuOpen(false);
          }} data-id="element-203">
                <PackageIcon className="h-4 w-4 mr-2" data-id="element-204" />
                Orders
              </Link>
              <Link to="/account/wishlist" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center" onClick={() => {
            setIsAccountOpen(false);
            setIsMenuOpen(false);
          }} data-id="element-205">
                <HeartIcon className="h-4 w-4 mr-2" data-id="element-206" />
                Wishlist
              </Link>
              <Link to="/account/settings" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center" onClick={() => {
            setIsAccountOpen(false);
            setIsMenuOpen(false);
          }} data-id="element-207">
                <SettingsIcon className="h-4 w-4 mr-2" data-id="element-208" />
                Settings
              </Link>
              <div className="border-t border-gray-100" data-id="element-209">
                <Link to="/logout" className="block px-4 py-2 hover:bg-gray-50 text-sm flex items-center text-red-500" onClick={() => {
              setIsAccountOpen(false);
              setIsMenuOpen(false);
              // Add logout logic here
              alert('You have been logged out');
            }} data-id="element-210">
                  <LogOutIcon className="h-4 w-4 mr-2" data-id="element-211" />
                  Logout
                </Link>
              </div>
            </div>
          </div>}
      </div>
    </header>;
};
export default Header;